import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentLifecycleRequest, HostedAgentListItem } from "@/api/admin-api"

import {
  Bot,
  ChevronDown,
  ChevronUp,
  Key,
  Pause,
  Pencil,
  Play,
  ServerCog,
  Shield,
  ShieldCheck,
  Trash2,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import { HostedAgentHealthBadge } from "./hosted-agent-health-badge"
import { HostedAgentLifecycleStatusBadge } from "./hosted-agent-lifecycle-status-badge"
import { isPolicyConfigured } from "../utils/access-policy"
import { formatHostedAgentTimestamp, summarizeHostedAgentBudget } from "../utils/hosted-agents"

const VISIBLE_ROWS = 5

export function HostedAgentsTable({
  agents,
  onEdit,
  onEditAccess,
  onOpenLifecycle,
  onPauseToggle,
  onRotateSecret,
  onDelete,
  pendingAgentId,
  lifecycleByAgentId,
  disableAll,
  emptyAction,
}: {
  agents: HostedAgentListItem[]
  /** Open the edit wizard for this agent (wired by the Fas 5 wizard). */
  onEdit?: (agentId: string) => void
  onEditAccess: (agentId: string) => void
  /** Open the wizard on the Lifecycle tab (upgrade / restart / stop / start). */
  onOpenLifecycle?: (agentId: string) => void
  /** Active (non-terminal) lifecycle request per agent id, for the status chip. */
  lifecycleByAgentId?: Map<string, HostedAgentLifecycleRequest>
  onPauseToggle: (agentId: string, paused: boolean) => void
  onRotateSecret: (agentId: string) => void
  onDelete: (agentId: string) => void
  /** Agent id with an in-flight row mutation — its actions are disabled. */
  pendingAgentId?: string
  disableAll?: boolean
  emptyAction?: React.ReactNode
}) {
  const { t, i18n } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const displayAgents = isExpanded ? agents : agents.slice(0, VISIBLE_ROWS)
  const hasMore = agents.length > VISIBLE_ROWS

  if (agents.length === 0) {
    return (
      <EmptyState
        icon={Bot}
        primaryAction={emptyAction}
        title={t("adminDashboard.hostedAgents.emptyTitle", { defaultValue: "No hosted agents" })}
        description={t("adminDashboard.hostedAgents.emptyDescription", {
          defaultValue: "Create your first role agent from a template or start from scratch.",
        })}
      />
    )
  }

  return (
    <div className="space-y-3">
      <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-sm font-medium lg:grid">
        <div className="col-span-4">
          {t("adminDashboard.hostedAgents.columns.agent", { defaultValue: "Agent" })}
        </div>
        <div className="col-span-2">
          {t("adminDashboard.hostedAgents.columns.model", { defaultValue: "Model" })}
        </div>
        <div className="col-span-2">
          {t("adminDashboard.hostedAgents.columns.status", { defaultValue: "Status" })}
        </div>
        <div className="col-span-2">
          {t("adminDashboard.hostedAgents.health.column", { defaultValue: "Health" })}
        </div>
        <div className="col-span-2 text-right">
          {t("adminDashboard.hostedAgents.columns.actions", { defaultValue: "Actions" })}
        </div>
      </div>

      {displayAgents.map((agent) => {
        const isPending = disableAll || pendingAgentId === agent.id
        const accessConfigured = isPolicyConfigured(agent.access)
        const lifecycle = lifecycleByAgentId?.get(agent.id)
        const budgetSummary = summarizeHostedAgentBudget(agent.budget_status)
        let budgetVariant: "destructive" | "warning" | "outline" = "outline"
        if (budgetSummary?.exceeded) {
          budgetVariant = "destructive"
        } else if ((budgetSummary?.percent ?? 0) >= 80) {
          budgetVariant = "warning"
        }
        return (
          <div
            key={agent.id}
            className="admin-responsive-grid-row group bg-card hover:bg-accent/50 grid grid-cols-12 gap-4 rounded-lg border p-4 text-sm transition-colors"
          >
            <div className="col-span-4 flex min-w-0 items-center gap-3">
              <span
                aria-hidden="true"
                className={`border-foreground flex size-9 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${agent.profile.avatar.color ? "text-white" : "text-chart-foreground"}`}
                style={{ backgroundColor: agent.profile.avatar.color || "hsl(var(--chart-1))" }}
              >
                {agent.profile.avatar.initials ||
                  agent.profile.name.slice(0, 2).toLocaleUpperCase()}
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{agent.profile.name}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {agent.profile.title ? `${agent.profile.title} · ` : ""}
                  <span className="font-mono">{agent.slug}</span>
                </span>
              </div>
            </div>

            <div className="col-span-2 flex items-center">
              <span className="text-muted-foreground mr-3 w-24 shrink-0 text-xs font-medium lg:hidden">
                {t("adminDashboard.hostedAgents.columns.model", { defaultValue: "Model" })}
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="truncate">
                  {agent.model.selection === "auto"
                    ? t("adminDashboard.hostedAgents.modelAuto", { defaultValue: "Auto" })
                    : agent.model.model_id}
                </span>
                <span className="text-muted-foreground text-xs">
                  {t("adminDashboard.hostedAgents.effort", {
                    defaultValue: "Effort: {{effort}}",
                    effort: t(
                      `adminDashboard.hostedAgents.effortValues.${agent.model.reasoning_effort}`,
                      { defaultValue: agent.model.reasoning_effort }
                    ),
                  })}
                </span>
              </div>
            </div>

            <div className="col-span-2 flex flex-wrap items-center gap-1.5">
              <span className="text-muted-foreground mr-3 w-24 shrink-0 text-xs font-medium lg:hidden">
                {t("adminDashboard.hostedAgents.columns.status", { defaultValue: "Status" })}
              </span>
              {agent.enabled ? (
                <Badge variant="success">
                  {t("adminDashboard.hostedAgents.statusEnabled", { defaultValue: "Enabled" })}
                </Badge>
              ) : (
                <Badge variant="disabled">
                  {t("adminDashboard.hostedAgents.statusDisabled", { defaultValue: "Disabled" })}
                </Badge>
              )}
              {agent.paused ? (
                <Badge variant="warning">
                  {t("adminDashboard.hostedAgents.statusPaused", { defaultValue: "Paused" })}
                </Badge>
              ) : null}
              {lifecycle ? (
                <HostedAgentLifecycleStatusBadge
                  action={lifecycle.action}
                  status={lifecycle.status}
                />
              ) : null}
              <Badge variant="outline">
                {agent.memory_mode === "open"
                  ? t("adminDashboard.hostedAgents.memoryOpen", { defaultValue: "Open memory" })
                  : t("adminDashboard.hostedAgents.memoryPersonal", {
                      defaultValue: "Personal memory",
                    })}
              </Badge>
              {budgetSummary ? (
                <Badge variant={budgetVariant}>
                  {budgetSummary.exceeded
                    ? t("adminDashboard.hostedAgents.budgetExceeded", {
                        defaultValue: "Budget exceeded",
                      })
                    : t("adminDashboard.hostedAgents.budgetPercent", {
                        defaultValue: "Budget {{percent}}%",
                        percent: Math.round(budgetSummary.percent),
                      })}
                </Badge>
              ) : null}
            </div>

            <div className="col-span-2 flex min-w-0 items-center">
              <span className="text-muted-foreground mr-3 w-24 shrink-0 text-xs font-medium lg:hidden">
                {t("adminDashboard.hostedAgents.health.column", { defaultValue: "Health" })}
              </span>
              {agent.health ? (
                // #3039: computed state + "last seen … · vX" (falls back to the
                // bare version chip on a backend without the health service).
                <HostedAgentHealthBadge health={agent.health} />
              ) : null}
              {!agent.health && agent.hermes_version.last_seen ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-muted-foreground cursor-help font-mono text-xs">
                        {agent.hermes_version.last_seen}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {t("adminDashboard.hostedAgents.hermesSeenAt", {
                          defaultValue: "Last seen: {{seenAt}}",
                          seenAt:
                            formatHostedAgentTimestamp(
                              agent.hermes_version.seen_at,
                              i18n.language
                            ) ?? "—",
                        })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : null}
              {!agent.health && !agent.hermes_version.last_seen ? (
                <span className="text-muted-foreground text-xs">
                  {t("adminDashboard.hostedAgents.hermesNeverSeen", { defaultValue: "Never seen" })}
                </span>
              ) : null}
            </div>

            <div className="col-span-2 flex items-center justify-end gap-1">
              <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                {t("adminDashboard.hostedAgents.columns.actions", { defaultValue: "Actions" })}
              </span>
              {onEdit ? (
                <Button
                  disabled={isPending}
                  size="icon"
                  type="button"
                  variant="ghost"
                  aria-label={t("adminDashboard.hostedAgents.editAriaLabel", {
                    defaultValue: "Edit hosted agent",
                  })}
                  onClick={() => onEdit(agent.id)}
                >
                  <Pencil className="size-4" />
                </Button>
              ) : null}
              <Button
                disabled={isPending}
                size="icon"
                type="button"
                variant="outline"
                aria-label={t("adminDashboard.hostedAgents.accessAriaLabel", {
                  defaultValue: "Access policy",
                })}
                onClick={() => onEditAccess(agent.id)}
              >
                {accessConfigured ? (
                  <ShieldCheck className="text-foreground size-4" />
                ) : (
                  <Shield className="size-4" />
                )}
              </Button>
              <Button
                disabled={isPending}
                size="icon"
                type="button"
                variant="ghost"
                aria-label={
                  agent.paused
                    ? t("adminDashboard.hostedAgents.resumeAriaLabel", {
                        defaultValue: "Resume agent",
                      })
                    : t("adminDashboard.hostedAgents.pauseAriaLabel", {
                        defaultValue: "Pause agent",
                      })
                }
                // Pause/resume only gates admission of new runs; the container
                // stop/start/upgrade actions live on the Lifecycle tab.
                title={
                  agent.paused
                    ? t("adminDashboard.hostedAgents.resumeHint", {
                        defaultValue:
                          "Resume: new runs are admitted again. Stop, start or upgrade the container under Lifecycle.",
                      })
                    : t("adminDashboard.hostedAgents.pauseHint", {
                        defaultValue:
                          "Pause: new runs are held; the container keeps running. Stop, start or upgrade the container under Lifecycle.",
                      })
                }
                onClick={() => onPauseToggle(agent.id, agent.paused)}
              >
                {agent.paused ? <Play className="size-4" /> : <Pause className="size-4" />}
              </Button>
              {onOpenLifecycle ? (
                <Button
                  disabled={isPending}
                  size="icon"
                  type="button"
                  variant="ghost"
                  aria-label={t("adminDashboard.hostedAgents.lifecycleAriaLabel", {
                    defaultValue: "Container lifecycle",
                  })}
                  onClick={() => onOpenLifecycle(agent.id)}
                >
                  <ServerCog className="size-4" />
                </Button>
              ) : null}
              <Button
                disabled={isPending}
                size="icon"
                type="button"
                variant="ghost"
                aria-label={t("adminDashboard.hostedAgents.rotateAriaLabel", {
                  defaultValue: "Rotate secret",
                })}
                onClick={() => onRotateSecret(agent.id)}
              >
                <Key className="size-4" />
              </Button>
              <Button
                disabled={isPending}
                size="icon"
                type="button"
                variant="ghost"
                aria-label={t("adminDashboard.hostedAgents.deleteAriaLabel", {
                  defaultValue: "Delete hosted agent",
                })}
                onClick={() => onDelete(agent.id)}
              >
                <Trash2 className="text-foreground size-4" />
              </Button>
            </div>
          </div>
        )
      })}

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button className="w-full" variant="outline" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? (
              <>
                <ChevronUp className="mr-2 size-4" />
                {t("adminDashboard.hostedAgents.showLess", { defaultValue: "Show Less" })}
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                {t("adminDashboard.hostedAgents.showMore", {
                  defaultValue: "Show {{count}} more",
                  count: agents.length - VISIBLE_ROWS,
                })}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
